import { MainContainer } from "../../component/container";
import { Card, CardContent } from "../../utils/Cards";
import { FaChartLine, FaMoneyBill, FaUser, FaArrowUp, FaArrowDown } from "react-icons/fa";
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    LabelList
} from 'recharts';
import { MdMail } from 'react-icons/md';
import { useEffect } from "react";
import axios from 'axios';
import dayjs from "dayjs";
import { useState } from "react";
import GratitudeLetter from "../../component/Gratitude";
import GratitudePreviewModal from "../../component/GratitudePreviewModal";
import * as html2pdf from 'html2pdf.js';

const url = process.env.REACT_APP_URL;

const OrdersOverview = ({ allTransactions }) => {
    const colorPool = [
        'bg-green-500',
        'bg-red-500',
        'bg-blue-500',
        'bg-orange-400',
        'bg-pink-500',
        'bg-yellow-400',
        'bg-purple-500'
    ];
    const orders = [...allTransactions]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .map(tx => ({
            color: colorPool[Math.floor(Math.random() * colorPool.length)],
            label: `$${parseFloat(tx.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}, by ${tx.given_name} ${tx.surname}`,
            date: dayjs(tx.date).format("MMM D, YYYY h:mm A")
        }));

    // 📈 Calculate % change: this month vs last month
    const thisMonth = dayjs().month();
    const lastMonth = dayjs().subtract(1, 'month').month();

    const thisMonthCount = allTransactions.filter(tx => dayjs(tx.date).month() === thisMonth).length;
    const lastMonthCount = allTransactions.filter(tx => dayjs(tx.date).month() === lastMonth).length;

    const percentChange = lastMonthCount === 0
        ? 100
        : Math.round(((thisMonthCount - lastMonthCount) / lastMonthCount) * 100);

    const isIncrease = percentChange >= 0;

    return (
        <div className="bg-[#1f2a46] rounded-xl shadow p-4 w-full xl:w-[30%]">
            <div className="text-xl font-semibold mb-2 text-white">Orders Overview</div>
            <p className={`text-sm font-semibold mb-4 ${isIncrease ? "text-green-500" : "text-red-500"}`}>
                {isIncrease ? <FaArrowUp className="inline" /> : <FaArrowDown className="inline" />} {Math.abs(percentChange)}% {isIncrease ? "more" : "less"} this month
            </p>

            <div className="space-y-6 max-h-[320px] overflow-y-auto pr-1">
                {orders.map((order, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                        <div className={`w-3 h-3 rounded-full mt-1 ${order.color}`}></div>
                        <div>
                            <p className="text-sm font-medium text-white">{order.label}</p>
                            <p className="text-xs text-white">{order.date}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const MonthlyTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const fullDate = dayjs(payload[0].payload.key + '-01').format('MMM YYYY');
        return (
            <div className="bg-white text-gray-500 text-sm px-2 py-1 rounded shadow">
                <p className="font-semibold">{fullDate}</p>
                <p>{`Value: ${payload[0].value}`}</p>
            </div>
        );
    }
    return null;
};

const WeeklyTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white text-gray-500 text-sm px-2 py-1 rounded shadow">
                <p className="font-semibold">{label}</p>
                <p>{`Value: ${payload[0].value}`}</p>
            </div>
        );
    }
    return null;
};



const ChartCard = ({ title, subtitle, footer, data, color, type = 'line', tooltipType, onPrev, onNext, showArrows = false }) => {
    return (
        <Card className="p-4 w-96">
            <div className={`rounded-lg p-4 ${color}`}>
                {showArrows ? (
                    <div className="flex justify-between items-center mb-2">
                        <button onClick={onPrev} className="text-white text-xl font-bold hover:text-gray-300 transition">&larr;</button>
                        <h4 className="text-white font-semibold text-md">{title}</h4>
                        <button onClick={onNext} className="text-white text-xl font-bold hover:text-gray-300 transition">&rarr;</button>
                    </div>
                ) : (
                    <h4 className="text-white font-semibold text-md mb-2">{title}</h4>
                )}

                <ResponsiveContainer width="100%" height={200}>
                    {type === 'bar' ? (
                        <BarChart data={data}>
                            <XAxis dataKey="name" stroke="white" />
                            <YAxis stroke="white" />
                            <Tooltip content={tooltipType === 'monthly' ? <MonthlyTooltip /> : <WeeklyTooltip />} />

                            <Bar dataKey="value" fill="white" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    ) : (
                        <LineChart data={data}>
                            <XAxis dataKey="name" stroke="white" />
                            <YAxis stroke="white" />
                            <Tooltip content={tooltipType === 'monthly' ? <MonthlyTooltip /> : <WeeklyTooltip />} />

                            <Line type="monotone" dataKey="value" stroke="white" strokeWidth={3} dot={{ fill: 'white' }}>
                                <LabelList dataKey="value" position="bottom" fill="white" />
                            </Line>
                        </LineChart>
                    )}
                </ResponsiveContainer>
            </div>

            <CardContent className="p-0 pt-4">
                <p className="text-sm text-white">{subtitle}</p>
                <p className="text-xs text-gray-400 mt-1">{footer}</p>
            </CardContent>
        </Card>
    );
};

const ProjectTable = ({ donors, setselected, years = [], currentYear, setCurrentYear }) => {
    const [autoSending, setAutoSending] = useState(false);

    const handlePrevYear = () => {
        const currentIndex = years.indexOf(currentYear);
        if (currentIndex < years.length - 1) {
            setCurrentYear(years[currentIndex + 1]);
        }
    };

    const handleNextYear = () => {
        const currentIndex = years.indexOf(currentYear);
        if (currentIndex > 0) {
            setCurrentYear(years[currentIndex - 1]);
        }
    };

    const filteredDonors = donors.filter(d => d.year === currentYear)[0]?.donors || [];

    const handleAutoSend = async () => {
        setAutoSending(true);

        const formDataList = [];

        for (const donor of filteredDonors) {
            const element = document.createElement('div');
            document.body.appendChild(element);

            const ReactDOMServer = await import('react-dom/server');
            const html = ReactDOMServer.renderToStaticMarkup(
                <div id="gratitude-preview">
                    <GratitudeLetter data={donor} year={currentYear} />
                </div>
            );
            element.innerHTML = html;

            const opt = {
                margin: 0.5,
                filename: `${donor.email}.pdf`,
                html2canvas: { scale: 2 },
                jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
            };

            const worker = html2pdf.default ? html2pdf.default() : html2pdf(); // support for both import types
            const blob = await worker.set(opt).from(element).outputPdf('blob');

            const file = new File([blob], `${donor.email}.pdf`, { type: 'application/pdf' });

            const formData = new FormData();
            formData.append('file', file);
            formData.append('to', donor.email);
            formData.append('name', donor.name);
            formData.append('year', currentYear);

            formDataList.push(formData);

            document.body.removeChild(element);
        }

        // Send files
        for (const formData of formDataList) {
            await fetch(url + `/send-gratitude-pdf`, {
                method: 'POST',
                body: formData,
            });
        }

        setAutoSending(false);
        alert('✅ All emails sent!');
    };


    return (
        <div className="bg-[#1f2a46] rounded-xl shadow p-4 w-full xl:w-[68%] text-white">
            <div className="flex justify-between items-center mb-4">
                <button
                    onClick={handlePrevYear}
                    className="text-white text-xl font-bold hover:text-gray-300 transition"
                >
                    &larr;
                </button>
                <div className="text-xl font-semibold">Top Donors - {currentYear}</div>
                <button
                    onClick={handleNextYear}
                    className="text-white text-xl font-bold hover:text-gray-300 transition"
                >
                    &rarr;
                </button>
            </div>

            <div className="flex justify-end mb-4">
                <button
                    onClick={handleAutoSend}
                    disabled={autoSending}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                    {autoSending ? 'Sending...' : 'Auto Send'}
                </button>
            </div>

            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-left">
                    <thead className="sticky top-0 bg-[#1f2a46]">
                        <tr className="text-white text-sm">
                            <th className="pb-2 text-left">NAME</th>
                            <th className="pb-2 text-left">EMAIL</th>
                            <th className="pb-2 text-left">AMOUNT</th>
                            <th className="pb-2 text-left">ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDonors.map((donor, idx) => (
                            <tr key={idx} className="border-t text-sm">
                                <td className="py-3">{idx + 1}. {donor.name}</td>
                                <td className="py-3">{donor.email}</td>
                                <td className="py-3">${donor.amount}</td>
                                <td className="py-3 cursor-pointer" onClick={() => setselected({ donor, currentYear })}>
                                    <MdMail />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


export default function Admin() {
    const [data, setData] = useState([]);
    const [selectedDonor, setSelectedDonor] = useState(null);
    const [weekIndex, setWeekIndex] = useState(0);
    const [monthIncomeIndex, setMonthIncomeIndex] = useState(0);
    const [monthDonorIndex, setMonthDonorIndex] = useState(0);
    const [currentYear, setCurrentYear] = useState(null);

    const maxMonthIndex = (data?.monthlyChunks?.length || 1) - 1;
    const maxDonorIndex = (data?.monthlyDonorChunks?.length || 1) - 1;
    const maxWeekIndex = (data?.weeklyData?.length || 1) - 1;

    const handlePrevWeek = () => {
        if (weekIndex < maxWeekIndex) setWeekIndex(weekIndex + 1);
    };

    const handleNextWeek = () => {
        if (weekIndex > 0) setWeekIndex(weekIndex - 1);
    };

    const handlePrevMonth = () => {
        if (monthIncomeIndex < maxMonthIndex) setMonthIncomeIndex(monthIncomeIndex + 1);
    };
    const handleNextMonth = () => {
        if (monthIncomeIndex > 0) setMonthIncomeIndex(monthIncomeIndex - 1);
    };

    const handlePrevDonor = () => {
        if (monthDonorIndex < maxDonorIndex) setMonthDonorIndex(monthDonorIndex + 1);
    };
    const handleNextDonor = () => {
        if (monthDonorIndex > 0) setMonthDonorIndex(monthDonorIndex - 1);
    };

    // useEffect(() => {
    //     axios.post('https://acrfi.sofkam.com/thisShouldFail')
    //         .then(res => console.log("Still works?", res))
    //         .catch(err => console.error("Expected fail:", err));
    // }, []);


    useEffect(() => {
        axios.post(url + '/getData').then((response) => {
            if (response) {
                // console.log("📊 Data fetched:", response.data);

                setData(response.data);
            }
        })
    }, []);


    useEffect(() => {
        if (data.topDonors?.length) {
            const years = data.topDonors.map(entry => entry.year).sort((a, b) => b - a);
            setCurrentYear(years[0]); // Most recent year
        }
    }, [data.topDonors]);

    return (
        <MainContainer page='ADMIN'>
            <div className="h-full pt-40 flex flex-col gap-2">
                <div className="flex gap-4 flex-wrap justify-center">
                    <Card className="flex items-center p-4 w-64">
                        <div className="flex items-center justify-center w-12 h-12 bg-gray-800 rounded-md mr-4">
                            <FaUser className="text-white text-xl" />
                        </div>
                        <CardContent className="p-0">
                            <div className="text-sm text-white">Total Donator</div>
                            <div className="text-2xl font-bold text-white">{data.totalUniqueDonors}</div>
                        </CardContent>
                    </Card>

                    <Card className="flex items-center p-4 w-64">
                        <div className="flex items-center justify-center w-12 h-12 bg-blue-800 rounded-md mr-4">
                            <FaChartLine className="text-white text-xl" />
                        </div>
                        <CardContent className="p-0">
                            <div className="text-sm text-white">Total Donation</div>
                            <div className="text-2xl font-bold text-white">{data.totalDonations}</div>
                        </CardContent>
                    </Card>

                    <Card className="flex items-center p-4 w-64">
                        <div className="flex items-center justify-center w-12 h-12 bg-green-800 rounded-md mr-4">
                            <FaMoneyBill className="text-white text-xl" />
                        </div>
                        <CardContent className="p-0">
                            <div className="text-sm text-white">Total Money in USD</div>
                            <div className="text-2xl font-bold text-white">{data.totalAmount}</div>
                        </CardContent>
                    </Card>
                </div>

                <div className="flex gap-3 flex-wrap justify-center mt-6">
                    <ChartCard
                        title="Weekly Incomes"
                        subtitle={`Week ${data?.weeklyData?.[weekIndex]?.weekRange || ''}`}
                        footer="Use arrows to switch"
                        data={data?.weeklyData?.[weekIndex]?.days || []}
                        color="bg-blue-500"
                        type="bar"
                        tooltipType="weekly"
                        onPrev={handlePrevWeek}
                        onNext={handleNextWeek}
                        showArrows={true}
                    />


                    <ChartCard
                        title="Monthly Incomes"
                        subtitle={`7 Last month Incomes.`}
                        footer="⬅️ ➡️ to browse"
                        data={data.monthlyChunks?.[monthIncomeIndex] || []}
                        color="bg-green-500"
                        type="line"
                        tooltipType="monthly"
                        onPrev={handlePrevMonth}
                        onNext={handleNextMonth}
                        showArrows={true}
                    />

                    <ChartCard
                        title="Monthly Donor Count"
                        subtitle={`7 Last month donor activity`}
                        footer="⏱️ just updated"
                        data={data.monthlyDonorChunks?.[monthDonorIndex] || []}
                        color="bg-gray-800"
                        type="line"
                        tooltipType="monthly"
                        onPrev={handlePrevDonor}
                        onNext={handleNextDonor}
                        showArrows={true}
                    />


                </div>


                <div className="flex flex-col xl:flex-row gap-4 mt-10 px-6 md:px-32">
                    <ProjectTable
                        donors={data.topDonors || []}
                        setselected={setSelectedDonor}
                        years={[...new Set((data.topDonors || []).map(d => d.year))].sort((a, b) => b - a)}
                        currentYear={currentYear}
                        setCurrentYear={setCurrentYear}
                    />
                    <OrdersOverview allTransactions={data.allTransactions || []} />

                </div>

                {/* <GratitudeLetter data={selectedDonor?.donor} year={selectedDonor?.currentYear} /> */}
                {selectedDonor && (
                    <GratitudePreviewModal
                        selected={selectedDonor}
                        onClose={() => setSelectedDonor(null)}
                    />
                )}
            </div>
        </MainContainer>
    );
}
